/*
 * Route-level authorization predicates (CPMT-5077).
 *
 * Every predicate here is derived from a rule that already exists, never
 * invented, so that adding a guard cannot lock out anyone the app already
 * lets through:
 *
 * - `canManageTenants` and `canManageCompany` are the predicates
 *   `getRoleBasedLandingPage` uses to decide where to send a user after login,
 *   and that module imports them back. A user the app would land on
 *   `/admin/tenants` or `/admin/users` can therefore always reach it.
 * - `canManageCompanySettings` mirrors the navigation's Admin > Company
 *   condition. Landing logic only decides where to send a user; it was never
 *   the rule for who may open the company page.
 * - `canManageGlobalMessages` and `canAdministerAssist` mirror the server-side
 *   check on the endpoint each page depends on. Where a page's data is
 *   refused by the server, its guard refuses the page too, rather than
 *   rendering it and letting the fetch fail.
 *
 * Scope note: a client-side guard stops a non-admin from loading the admin
 * interface, which is the finding CPMT-5077 reports. It is not the security
 * boundary — the bundle ships to the browser. Data access is governed by
 * hub-backend's service-layer `@PreAuthorize`; the controller-level audit is
 * tracked separately.
 *
 * Permission checks read `rolesList[0]`, matching the existing convention in
 * `getRoleBasedLandingPage`.
 */

const getRole = user => (user && user.rolesList && user.rolesList[0]) || {};

const getPermissions = user => getRole(user).permissions || {};

const COMPANY_ADMIN_ROLES = [
  'OWNER',
  'ADMIN',
  'NARVAR_ADMIN',
  'NARVAR_SUPER_ADMIN',
];

/** Tenant administration: `/admin/tenants` and its detail pages. */
export const canManageTenants = user => {
  const {
    tenant_mgmt: tenantMgmt,
    tenant_mgmt_limited: tenantMgmtLimited,
  } = getPermissions(user);

  return Boolean(
    ((tenantMgmt && tenantMgmt.list) ||
      (tenantMgmtLimited && tenantMgmtLimited.list)) &&
      !(user && user.customerSupportLogin),
  );
};

/**
 * Company and user administration, as landing sees it: `/admin/users`.
 * Not used for `/admin/company`; see `canManageCompanySettings`.
 */
export const canManageCompany = user => {
  const {
    company_management: companyMgmt,
    user_management: userMgmt,
  } = getPermissions(user);
  const { name = '' } = getRole(user);

  return Boolean(
    (companyMgmt && companyMgmt.all && COMPANY_ADMIN_ROLES.includes(name)) ||
      (userMgmt && userMgmt.admin),
  );
};

/**
 * Company settings: `/admin/company`.
 *
 * Mirrors hum-navigation's Admin > Company condition, which sits inside what
 * the page's data allows. `/api/company`
 * (`TenantsApiTokenServices.loadCompanyResponseWithAccountKey`) needs
 * `companyManagement:all` and refuses NARVAR_AUTHOR only on Narvar's own
 * tenant. A NARVAR_AUTHOR in support mode is on the retailer's tenant, sees
 * the nav link and is served the data, so is admitted.
 *
 * Unlike `canManageCompany`, `user_management.admin` alone does not admit:
 * neither the nav nor `/api/company` allows it without
 * `company_management.all`.
 */
export const canManageCompanySettings = user => {
  const { company_management: companyMgmt } = getPermissions(user);
  const { name = '' } = getRole(user);
  const isNarvarAuthorInSupportMode =
    name === 'NARVAR_AUTHOR' && Boolean(user && user.customerSupportLogin);

  return Boolean(
    companyMgmt &&
      companyMgmt.all &&
      (COMPANY_ADMIN_ROLES.includes(name) || isNarvarAuthorInSupportMode),
  );
};

const NARVAR_TENANT_MONIKER = 'narvar';

/*
 * Whether the session is operating in Narvar's own tenant. Mirrors
 * hub-backend's `checkIfCurrentTenantForNarvarTenant`, which compares the
 * current retailer's URI moniker to "NARVAR". It is false while impersonating
 * a retailer, because support mode switches the current tenant to that
 * retailer — which is why `isNarvarInternalUser` (true in support mode) is
 * not a substitute for this check.
 */
const isInNarvarTenant = user => {
  if (!user) return false;

  const { retailerId, retailerIdToRetailerInfo = {} } = user;
  const { uri_moniker: moniker = '' } =
    retailerIdToRetailerInfo[retailerId] || {};

  return moniker.toLowerCase() === NARVAR_TENANT_MONIKER;
};

/**
 * Global broadcast messages: `/admin/messages` and its create/detail pages.
 *
 * Mirrors the server rule on `/api/global_broadcast_message`,
 * `checkUserPermissionsAndIsNarvarTenant({'globalBroadcastMessage:all'})`.
 * Without the tenant half, an impersonating CSM passes the guard, the page's
 * fetch is refused with a 403, and the generic 403 handler in
 * `lib/api/request.js` bounces them to `/`.
 */
export const canManageGlobalMessages = user => {
  const { global_broadcast_message: globalMessages } = getPermissions(user);

  return Boolean(
    isInNarvarTenant(user) && globalMessages && globalMessages.all,
  );
};

const NARVAR_ADMIN_ROLES = ['NARVAR_ADMIN', 'NARVAR_SUPER_ADMIN'];

/**
 * Assist administration: `/assist/admin`.
 *
 * Mirrors the server gate on `/api/toran/v1/assist/invalidate/:uriMoniker` in
 * `server/toranRouter.js`: `isNarvarAdminOrIsCustomerSupportLogin` and then
 * `checkPermissions({ role: 'designer', permissions: 'all' })`. The page
 * describes invalidation as re-fetching content for all retailers and pushing
 * it to production, so it is a Narvar operation rather than a retailer one.
 * Like the server helper, any role in `rolesList` counts.
 */
export const canAdministerAssist = user => {
  if (!user) return false;

  const { rolesList = [], customerSupportLogin } = user;
  const isNarvarAdminOrSupportMode =
    Boolean(customerSupportLogin) ||
    rolesList.some(role => NARVAR_ADMIN_ROLES.includes(role.name));
  const { designer } = getPermissions(user);

  return Boolean(isNarvarAdminOrSupportMode && designer && designer.all);
};
